import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Divider,
} from "@mui/material";
import { FormattedMessage } from "react-intl";
import { v4 as uuidv4 } from "uuid";
import { PlusOutlined } from "@ant-design/icons";
import { useTheme } from "@mui/material/styles";

// project import
import MainCard from "components/MainCard";
import { OutlineButton } from "components/buttons/OutlineButton";
import CheckboxField from "components/CheckboxField";
import { ReactComponent as BinIcon } from "assets/images/icons/icn_btn_bin.svg";
import { ReactComponent as BinHoverIcon } from "assets/images/icons/icn_btn_bin_hover.svg";
import {
  MSP_DAILY_CREDITS,
  ORG_SERVICE_STATUS_PRO_KEY,
  UNITY_PRO_ACCESS_POINT_DAILY_CREDITS,
  UNITY_PRO_SERVICE_KEY,
  UNITY_PRO_SWITCH_DAILY_CREDITS,
} from "const/license";

// utils
import localStorageUtil from "utils/localStorageUtil";

// Hooks
import useAuthScope from "hooks/useAuthScope";
import useDataSourceManager from "hooks/useDataSourceManager";

// ==============================|| COMPONENTS ||============================== ||

const TrashIconButton = ({ onClick, size = "20px", sx = {} }) => {
  const [isHovered, setIsHovered] = React.useState(false);
  return (
    <IconButton
      size='small'
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onClick}
      sx={{ p: 0.5, ...sx }}
    >
      {isHovered ? (
        <BinHoverIcon style={{ width: size, height: size }} />
      ) : (
        <BinIcon style={{ width: size, height: size }} />
      )}
    </IconButton>
  );
};

// ==============================|| CONSTANTS ||============================== ||

const getSiteName = (index) => {
  let value = index + 1;
  let suffix = "";

  while (value > 0) {
    value -= 1;
    suffix = String.fromCharCode(65 + (value % 26)) + suffix;
    value = Math.floor(value / 26);
  }

  return `Site ${suffix}`;
};

const getDefaultOrgs = () => [
  {
    id: uuidv4(),
    name: "TechCorp HQ",
    sites: [
      {
        id: uuidv4(),
        name: getSiteName(0),
        accessPoints: 20,
        switches: 10,
        [UNITY_PRO_SERVICE_KEY]: false,
      },
    ],
  },
];

const normalizeOrg = (org) => ({
  id: org?.id || uuidv4(),
  name: org?.name || "Organization",
  sites: Array.isArray(org?.sites)
    ? org.sites.map((site) => ({
      ...site,
      id: site.id || uuidv4(),
      [UNITY_PRO_SERVICE_KEY]:
        site[UNITY_PRO_SERVICE_KEY] ??
        site[ORG_SERVICE_STATUS_PRO_KEY] ??
        false,
    }))
    : [],
});

const createOrgFromServiceStatus = (org) => ({
  id: org?.id || "current-organization",
  name: org?.name || "Organization",
  sites: Array.isArray(org?.sites)
    ? org.sites.map((site) => ({
      id: site.id,
      name: site.name,
      accessPoints: site.accessPoints || 0,
      switches: site.switches || 0,
      [UNITY_PRO_SERVICE_KEY]:
        site.services?.[UNITY_PRO_SERVICE_KEY] === true,
    }))
    : [],
});

// ==============================|| CALCULATION LOGIC ||============================== ||

const calculateSiteDailyBurn = (site) => {
  if (!site[UNITY_PRO_SERVICE_KEY]) return 0;

  const accessPointCount = Number(site.accessPoints) || 0;
  const switchCount = Number(site.switches) || 0;
  return (
    accessPointCount * UNITY_PRO_ACCESS_POINT_DAILY_CREDITS +
    switchCount * UNITY_PRO_SWITCH_DAILY_CREDITS
  );
};

const calculateOrgDailyBurn = (org) => {
  if (!org) return 0;

  const sitesBurn = org.sites.reduce(
    (acc, site) => acc + calculateSiteDailyBurn(site),
    0,
  );
  return sitesBurn;
};

const STORAGE_KEY = "usage_emulator_config";

// ==============================|| USAGE EMULATOR PAGE ||============================== ||

const UsageEmulator = () => {
  const theme = useTheme();

  const [orgs, setOrgs] = useState([]);
  const { auth, standalone, isAuthReady } = useAuthScope();
  const { sendBatch } = useDataSourceManager();
  const [currentBalance, setCurrentBalance] = useState(0);
  const [isBalanceLoading, setIsBalanceLoading] = useState(false);
  const [unityMspProEnabled, setUnityMspProEnabled] = useState(false);
  const initialOrgRef = useRef(null);

  // Derived calculations
  const totalDailyBurn = useMemo(() => {
    return (
      calculateOrgDailyBurn(orgs[0]) +
      (standalone && unityMspProEnabled ? MSP_DAILY_CREDITS : 0)
    );
  }, [orgs, standalone, unityMspProEnabled]);

  // 計算預估可用天數 (Est. Lifetime = 餘額 / 每日消耗)
  const estimatedLifetime = useMemo(() => {
    if (totalDailyBurn === 0) return "∞";
    const days = Math.floor(currentBalance / totalDailyBurn);
    return days < 0 ? 0 : days;
  }, [currentBalance, totalDailyBurn]);

  const serviceStatusUuid = useMemo(() => uuidv4(), []);

  const getIds = () => ({
    realmId: auth.realm?.id || "",
    orgId: auth.orgs?.id || "",
  });

  const fetchLicenseServiceStatus = () => {
    const { realmId, orgId } = getIds();
    const apiKey = standalone
      ? "/orgs/:orgId/license/service/status.read"
      : "/ents/:entId/orgs/:orgId/license/service/status.read";
    const restfulPlaceholders = standalone
      ? { orgId: realmId }
      : { entId: realmId, orgId };

    return {
      apiKey,
      restfulPlaceholders,
      params: {},
      requestUuid: serviceStatusUuid,
    };
  };

  const textfieldStyle = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "20px",
      height: "34px",
      "& fieldset": {
        borderColor: "#172664",
      },
      "&:hover fieldset": {
        borderColor: theme.palette.primary.main,
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    "& .MuiOutlinedInput-input": {
      fontSize: "13px",
      color: "#172664",
      py: 0,
    },
  };

  // 載入時取得當前點數餘額 (Current Balance)
  useEffect(() => {
    const getBalance = async () => {
      if (!isAuthReady) return;
      initialOrgRef.current = null;
      setIsBalanceLoading(true);
      try {
        const { getSendBatchData } = await sendBatch([
          fetchLicenseServiceStatus(),
        ]);
        const serviceStatusRes = getSendBatchData({
          requestUuid: serviceStatusUuid,
        });
        if (serviceStatusRes?.data?.org?.walletBalance !== undefined) {
          setCurrentBalance(
            Number(serviceStatusRes.data.org.walletBalance) || 0,
          );
        }
        const serviceStatusOrg = serviceStatusRes?.data?.org;

        if (serviceStatusOrg) {
          const initialOrg = createOrgFromServiceStatus(serviceStatusOrg);
          initialOrgRef.current = initialOrg;

          setOrgs((currentOrgs) => {
            const currentSites = new Map(
              (currentOrgs[0]?.sites || []).map((site) => [site.id, site]),
            );
            const statusOrg = createOrgFromServiceStatus(serviceStatusOrg);

            return [{
              ...statusOrg,
              sites: statusOrg.sites.map((site) => ({
                ...site,
                [UNITY_PRO_SERVICE_KEY]:
                  currentSites.get(site.id)?.[UNITY_PRO_SERVICE_KEY] ??
                  site[UNITY_PRO_SERVICE_KEY],
              })),
            }];
          });
        }
      } catch (error) {
        console.error("[UsageEmulator] Error fetching balance:", error);
      } finally {
        setIsBalanceLoading(false);
      }
    };
    getBalance();
  }, [isAuthReady, auth.orgs?.id, auth.realm?.id]);

  // Load from local storage on mount
  useEffect(() => {
    const saved = localStorageUtil.getItem(STORAGE_KEY);
    if (saved && Array.isArray(saved)) {
      setOrgs(saved.length > 0 ? [normalizeOrg(saved[0])] : getDefaultOrgs());
    } else if (Array.isArray(saved?.orgs)) {
      setOrgs(saved.orgs.length > 0 ? [normalizeOrg(saved.orgs[0])] : getDefaultOrgs());
      setUnityMspProEnabled(saved.unityMspProEnabled === true);
    } else {
      setOrgs(getDefaultOrgs());
    }
  }, []);

  // Handlers
  const handleAddSite = (orgId) => {
    setOrgs(
      orgs.map((o) => {
        if (o.id === orgId) {
          return {
            ...o,
            sites: [
              ...o.sites,
              {
                id: uuidv4(),
                name: getSiteName(o.sites.length),
                accessPoints: 0,
                switches: 0,
                [UNITY_PRO_SERVICE_KEY]: false,
              },
            ],
          };
        }
        return o;
      }),
    );
  };

  const handleDeleteSite = (orgId, siteId) => {
    setOrgs(
      orgs.map((o) => {
        if (o.id === orgId) {
          return {
            ...o,
            sites: o.sites.filter((s) => s.id !== siteId),
          };
        }
        return o;
      }),
    );
  };

  const handleUpdateSite = (orgId, siteId, fields) => {
    setOrgs(
      orgs.map((o) => {
        if (o.id === orgId) {
          return {
            ...o,
            sites: o.sites.map((s) =>
              s.id === siteId ? { ...s, ...fields } : s,
            ),
          };
        }
        return o;
      }),
    );
  };

  const handleReset = () => {
    setOrgs(
      initialOrgRef.current
        ? [normalizeOrg(initialOrgRef.current)]
        : getDefaultOrgs(),
    );
    setUnityMspProEnabled(false);
  };

  const handleExport = () => {
    const exportData = { orgs: orgs.slice(0, 1), unityMspProEnabled };

    const dataStr =
      "data:text/json;charset=utf-8," +
      encodeURIComponent(JSON.stringify(exportData, null, 2));
    const downloadAnchorNode = document.createElement("a");
    downloadAnchorNode.setAttribute("href", dataStr);
    downloadAnchorNode.setAttribute("download", "usage_emulator_config.json");
    document.body.appendChild(downloadAnchorNode);
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };

  const handleImport = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const json = JSON.parse(e.target.result);
          if (Array.isArray(json) && json.length > 0) {
            setOrgs([normalizeOrg(json[0])]);
            setUnityMspProEnabled(false);
          } else if (Array.isArray(json?.orgs) && json.orgs.length > 0) {
            setOrgs([normalizeOrg(json.orgs[0])]);
            setUnityMspProEnabled(json.unityMspProEnabled === true);
          } else {
            throw new Error("Invalid usage emulator configuration");
          }
        } catch (err) {
          alert("Invalid JSON file");
        }
      };
      reader.readAsText(file);
    }
  };

  return (
    <Box>
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        sx={{
          mb: 3,
          height: "64px",
          mt: -1,
        }}
      >
        <Typography
          variant='h4'
          sx={{
            color: "#172664",
            fontFamily: theme.typography.fontFamily,
            fontSize: "22px",
            fontWeight: "bold",
          }}
        >
          <FormattedMessage id='usage-emulator' />
        </Typography>

        <Stack
          direction='row'
          alignItems='center'
          spacing={2}
          sx={{ height: "100%" }}
        >
          <Divider
            orientation='vertical'
            sx={{
              height: "40px",
              borderColor: "rgba(0,0,0,0.08)",
              borderRightWidth: "1px",
              mx: 1,
            }}
          />
          <OutlineButton onClick={handleReset} height='34px'>
            Reset
          </OutlineButton>
          <OutlineButton onClick={handleExport} height='34px'>
            Export
          </OutlineButton>
          <OutlineButton component='label' height='34px'>
            Import
            <input type='file' hidden accept='.json' onChange={handleImport} />
          </OutlineButton>
        </Stack>
      </Stack>

      <Grid container spacing={4}>
        {/* Simulated Result Section */}
        <Grid item xs={12}>
          <MainCard
            content={false}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "20px",
            }}
          >
            <Box sx={{ p: 4 }}>
              {/* 使用 spacing={8} 並加上 flex 包裝，確保小螢幕也能完美排列 */}
              <Stack direction='row' spacing={8} useFlexGap flexWrap='wrap'>
                {/* 1. Current Balance 卡片 */}
                <Box>
                  <Typography
                    sx={{
                      color: "#172664",
                      fontWeight: "bold",
                      fontSize: "15px",
                      mb: 1,
                    }}
                  >
                    Current Balance
                  </Typography>
                  <Stack spacing={0.5}>
                    <Typography
                      sx={{
                        fontSize: "42px",
                        fontWeight: "bold",
                        color: "#008514",
                        lineHeight: 1,
                      }}
                    >
                      {isBalanceLoading
                        ? "-"
                        : currentBalance.toLocaleString() || 0}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#373737",
                        fontWeight: "bold",
                      }}
                    >
                      Credits
                    </Typography>
                  </Stack>
                </Box>

                {/* 2. Daily Usage 卡片 */}
                <Box>
                  <Typography
                    sx={{
                      color: "#172664",
                      fontWeight: "bold",
                      fontSize: "15px",
                      mb: 1,
                    }}
                  >
                    Daily Usage
                  </Typography>
                  <Stack spacing={0.5}>
                    <Typography
                      sx={{
                        fontSize: "42px",
                        fontWeight: "bold",
                        color: "#008514",
                        lineHeight: 1,
                      }}
                    >
                      {totalDailyBurn.toLocaleString() || 0}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#373737",
                        fontWeight: "bold",
                      }}
                    >
                      Credits
                    </Typography>
                  </Stack>
                </Box>

                {/* 3. Month Usage 卡片 */}
                <Box>
                  <Typography
                    sx={{
                      color: "#172664",
                      fontWeight: "bold",
                      fontSize: "15px",
                      mb: 1,
                    }}
                  >
                    Month Usage (30 Days)
                  </Typography>
                  <Stack spacing={0.5}>
                    <Typography
                      sx={{
                        fontSize: "42px",
                        fontWeight: "bold",
                        color: "#008514",
                        lineHeight: 1,
                      }}
                    >
                      {(totalDailyBurn * 30).toLocaleString() || 0}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#373737",
                        fontWeight: "bold",
                      }}
                    >
                      Credits
                    </Typography>
                  </Stack>
                </Box>

                {/* 5. Year Usage 卡片 */}
                <Box>
                  <Typography
                    sx={{
                      color: "#172664",
                      fontWeight: "bold",
                      fontSize: "15px",
                      mb: 1,
                    }}
                  >
                    Year Usage (365 Days)
                  </Typography>
                  <Stack spacing={0.5}>
                    <Typography
                      sx={{
                        fontSize: "42px",
                        fontWeight: "bold",
                        color: "#008514",
                        lineHeight: 1,
                      }}
                    >
                      {(totalDailyBurn * 365).toLocaleString() || 0}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#373737",
                        fontWeight: "bold",
                      }}
                    >
                      Credits
                    </Typography>
                  </Stack>
                </Box>

                <Box>
                  <Typography
                    sx={{
                      color: "#172664",
                      fontWeight: "bold",
                      fontSize: "15px",
                      mb: 1,
                    }}
                  >
                    Estimated Lifetime
                  </Typography>
                  <Stack spacing={0.5}>
                    <Typography
                      sx={{
                        fontSize: "42px",
                        fontWeight: "bold",
                        color: "#008514",
                        lineHeight: 1,
                      }}
                    >
                      {isBalanceLoading ? "-" : estimatedLifetime.toLocaleString()}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#373737",
                        fontWeight: "bold",
                      }}
                    >
                      Days
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </Box>
          </MainCard>
        </Grid>

        {/* Configuration Section */}
        <Grid item xs={12}>
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
            sx={{ mb: 2 }}
          >
            <Stack spacing={0.5}>
              <Typography
                sx={{
                  color: "#172664",
                  fontSize: "18px",
                  fontWeight: "bold",
                }}
              >
                Configuration
              </Typography>
              <Typography sx={{ fontSize: "14px", color: "text.secondary" }}>
                Edits below update the simulated results above.
              </Typography>
              {standalone && (
                <Box sx={{ mt: 1 }}>
                  <CheckboxField
                    label={`Unity MSP Pro (${MSP_DAILY_CREDITS} Credits/day)`}
                    checked={unityMspProEnabled}
                    onChange={setUnityMspProEnabled}
                  />
                </Box>
              )}
            </Stack>
          </Stack>

          <Stack spacing={4}>
            {orgs.slice(0, 1).map((org) => (
              <MainCard
                key={org.id}
                content={false}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: "20px",
                }}
              >
                <Box sx={{ p: 4 }}>
                  <Stack spacing={3}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "18px",
                          fontWeight: "bold",
                          color: "#172664",
                        }}
                      >
                        {org.name}
                      </Typography>
                      <Stack direction='row' spacing={2} alignItems='center'>
                        <OutlineButton
                          size='small'
                          startIcon={
                            <PlusOutlined
                              style={{
                                border: "1.5px solid",
                                borderRadius: "50%",
                                padding: "1px",
                                fontSize: "10px",
                              }}
                            />
                          }
                          onClick={() => handleAddSite(org.id)}
                          width='auto'
                          height='30px'
                          sx={{
                            borderRadius: "8px",
                            color: "#172664",
                            borderColor: "#172664",
                          }}
                        >
                          Add Site
                        </OutlineButton>
                      </Stack>
                    </Box>

                    <TableContainer>
                      <Table size='small'>
                        <TableHead>
                          {/* 透過 CSS 選擇器，讓此 Row 下的所有標題單元格 (th) 統一向頂部對齊，達成完美的第一行水平對齊 */}
                          <TableRow sx={{ "& th": { verticalAlign: "top" } }}>
                            <TableCell
                              sx={{
                                fontSize: "14px",
                                fontWeight: "bold",
                                color: "#172664",
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                pb: 2,
                                width: "30%",
                              }}
                            >
                              Site
                            </TableCell>
                            <TableCell
                              align='center'
                              sx={{
                                fontSize: "14px",
                                fontWeight: "bold",
                                color: "#172664",
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                pb: 2,
                              }}
                            >
                              Access Points
                            </TableCell>
                            <TableCell
                              align='center'
                              sx={{
                                fontSize: "14px",
                                fontWeight: "bold",
                                color: "#172664",
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                pb: 2,
                              }}
                            >
                              Switches
                            </TableCell>
                            <TableCell
                              align='center'
                              sx={{
                                fontSize: "14px",
                                fontWeight: "bold",
                                color: "#172664",
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                pb: 2,
                              }}
                            >
                              <Stack spacing={0.5} alignItems='center'>
                                <Box>Nuclias Unity Pro</Box>
                                <Typography
                                  sx={{
                                    fontSize: "11px",
                                    color: "text.secondary",
                                    fontWeight: "normal",
                                  }}
                                >
                                  {UNITY_PRO_ACCESS_POINT_DAILY_CREDITS} Credits/device/day
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell
                              align='right'
                              sx={{
                                fontSize: "14px",
                                fontWeight: "bold",
                                color: "#172664",
                                borderBottom: `1px solid ${theme.palette.divider}`,
                                pb: 2,
                              }}
                            >
                              Action
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {org.sites.map((site, index) => (
                            <TableRow
                              key={site.id}
                              sx={{
                                bgcolor:
                                  index % 2 === 1 ? "grey.50" : "transparent",
                                "& td": { borderBottom: "none", py: 1.5 },
                              }}
                            >
                              <TableCell>
                                <TextField
                                  size='small'
                                  fullWidth
                                  variant='outlined'
                                  value={site.name}
                                  onChange={(e) =>
                                    handleUpdateSite(org.id, site.id, {
                                      name: e.target.value,
                                    })
                                  }
                                  sx={textfieldStyle}
                                />
                              </TableCell>
                              <TableCell align='center'>
                                <TextField
                                  type='text'
                                  size='small'
                                  sx={{ ...textfieldStyle, width: 120 }}
                                  value={site.accessPoints}
                                  onChange={(e) => {
                                    const val = e.target.value.replace(
                                      /\D/g,
                                      "",
                                    );
                                    if (val === "" || Number(val) <= 100000) {
                                      handleUpdateSite(org.id, site.id, {
                                        accessPoints: val,
                                      });
                                    }
                                  }}
                                />
                              </TableCell>
                              <TableCell align='center'>
                                <TextField
                                  type='text'
                                  size='small'
                                  sx={{ ...textfieldStyle, width: 120 }}
                                  value={site.switches}
                                  onChange={(e) => {
                                    const val = e.target.value.replace(
                                      /\D/g,
                                      "",
                                    );
                                    if (val === "" || Number(val) <= 100000) {
                                      handleUpdateSite(org.id, site.id, {
                                        switches: val,
                                      });
                                    }
                                  }}
                                />
                              </TableCell>
                              <TableCell align='center'>
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                  }}
                                >
                                  <CheckboxField
                                    checked={site[UNITY_PRO_SERVICE_KEY]}
                                    onChange={(checked) =>
                                      handleUpdateSite(org.id, site.id, {
                                        [UNITY_PRO_SERVICE_KEY]: checked,
                                      })
                                    }
                                  />
                                </Box>
                              </TableCell>
                              <TableCell align='right'>
                                <TrashIconButton
                                  onClick={() =>
                                    handleDeleteSite(org.id, site.id)
                                  }
                                  size='20px'
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Stack>
                </Box>
              </MainCard>
            ))}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UsageEmulator;
